// Kaart in het lead-detail: de gespreksvoorbereiding (vragen om te stellen, aannames, aanpak).
// De inhoud wordt automatisch gemaakt bij een nieuwe website-lead (edge function lead-voorbereiding),
// en kan hier voor elke lead op verzoek worden gemaakt of opnieuw gemaakt.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";

const C = { border: "#1c2a44", text: "#e6ecf5", muted: "#8da0bd", dim: "#5d6f8c", accent: "#a78bfa", yellow: "#f5b942", red: "#f87171", green: "#34d399" };

const lijstStijl = { margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.65, color: C.text };
const Kop = ({ children }) => (
  <p style={{ margin: "16px 0 6px", fontSize: 11, letterSpacing: ".07em", textTransform: "uppercase", color: C.accent, fontWeight: 700 }}>{children}</p>
);
const Blok = ({ children }) => (
  <div style={{ borderLeft: "3px solid " + C.accent, background: "rgba(167,139,250,.07)", padding: "10px 12px", fontSize: 13, lineHeight: 1.65, whiteSpace: "pre-wrap", color: C.text }}>{children}</div>
);

function alsTekst(naam, i) {
  const l = (t, items) => t + "\n" + (items || []).map((x) => "- " + x).join("\n") + "\n";
  return [
    "GESPREKSVOORBEREIDING: " + naam, "", "Eerste actie: " + i.eerste_actie, "",
    "IN HET KORT", i.samenvatting, "",
    "WAT DE KLANT WIL BEREIKEN", i.doel_klant, "",
    l("WAT WE WETEN", i.wat_we_weten), l("AANNAMES OM TE TOETSEN", i.aannames),
    "VRAGEN VOOR HET GESPREK",
    ...(i.vragen || []).flatMap((t) => [t.thema, ...t.vragen.map((v, n) => (n + 1) + ". " + v.vraag + " (waarom: " + v.waarom + ")"), ""]),
    "OPLOSSINGSRICHTING", i.oplossingsrichting, "",
    "AANPAK", ...(i.aanpak || []).map((f) => f.fase + ": " + f.inhoud), "",
    l("RISICO'S", i.risicos),
    "BUDGET EN PLANNING", i.budget_en_planning, "",
    "OPENING: " + i.gespreksopzet?.opening, "AFSLUITING: " + i.gespreksopzet?.afsluiting,
  ].join("\n");
}

export default function GespreksVoorbereiding({ leadId, leadNaam }) {
  const [rij, setRij] = useState(undefined);   // undefined = laden, null = geen voorbereiding
  const [bezig, setBezig] = useState(false);
  const [bericht, setBericht] = useState("");
  const [afgevinkt, setAfgevinkt] = useState({});
  const sleutel = "voorbereiding_afgevinkt_" + leadId;

  const laad = useCallback(async () => {
    const { data } = await supabase.from("lead_voorbereidingen").select("*").eq("lead_id", leadId).maybeSingle();
    setRij(data || null);
  }, [leadId]);

  useEffect(() => {
    setRij(undefined); setBericht("");
    try { setAfgevinkt(JSON.parse(localStorage.getItem(sleutel) || "{}")); } catch { setAfgevinkt({}); }
    laad();
    const ch = supabase.channel("voorbereiding_" + leadId)
      .on("postgres_changes", { event: "*", schema: "public", table: "lead_voorbereidingen", filter: "lead_id=eq." + leadId }, () => laad())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [leadId, laad, sleutel]);

  // Zolang de voorbereiding wordt gemaakt, elke 8 seconden opnieuw kijken (vangnet naast realtime).
  useEffect(() => {
    if (rij?.status !== "bezig") return;
    const t = setInterval(laad, 8000);
    return () => clearInterval(t);
  }, [rij?.status, laad]);

  function vink(k) {
    setAfgevinkt((p) => {
      const n = { ...p, [k]: !p[k] };
      try { localStorage.setItem(sleutel, JSON.stringify(n)); } catch { /* geen probleem */ }
      return n;
    });
  }

  async function maak(force) {
    setBezig(true); setBericht("");
    try {
      const { error } = await supabase.functions.invoke("lead-voorbereiding", { body: { lead_id: leadId, force } });
      if (error) throw error;
      setRij({ status: "bezig" });
      setBericht("Wordt gemaakt. Dit duurt ongeveer een minuut, je ziet het hier vanzelf verschijnen.");
    } catch (e) {
      setBericht("Starten lukte niet: " + (e?.message || e));
    } finally { setBezig(false); }
  }

  async function kopieer() {
    try { await navigator.clipboard.writeText(alsTekst(leadNaam, rij.inhoud)); setBericht("Gekopieerd naar het klembord."); }
    catch { setBericht("Kopiëren lukte niet."); }
  }

  const i = rij?.status === "klaar" ? rij.inhoud : null;

  return (
    <div className="card" style={{ marginBottom: 13 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <p className="lbl" style={{ margin: 0 }}>Gespreksvoorbereiding</p>
        <div style={{ flex: 1 }} />
        {i && <button className="btn bg" style={{ fontSize: 11 }} onClick={kopieer}>Kopieer als tekst</button>}
        {i && <button className="btn bg" style={{ fontSize: 11 }} disabled={bezig} onClick={() => maak(true)}>Opnieuw maken</button>}
      </div>

      {rij === undefined && <p style={{ fontSize: 12, color: C.dim, marginTop: 10 }}>Laden...</p>}

      {rij === null && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 10 }}>Nog geen voorbereiding voor deze lead. Ik maak een overzicht met de vragen die je in het gesprek kunt stellen, de aannames om te toetsen en een voorstel voor de aanpak.</p>
          <button className="btn bp" disabled={bezig} onClick={() => maak(false)}>{bezig ? "Bezig..." : "Maak gespreksvoorbereiding"}</button>
        </div>
      )}

      {rij?.status === "bezig" && <p style={{ fontSize: 12, color: C.yellow, marginTop: 10 }}>De voorbereiding wordt gemaakt. Dit duurt ongeveer een minuut.</p>}

      {rij?.status === "mislukt" && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 12, color: C.red, marginBottom: 8 }}>Maken lukte niet{rij.fout ? ": " + rij.fout : "."}</p>
          <button className="btn bp" disabled={bezig} onClick={() => maak(true)}>{bezig ? "Bezig..." : "Opnieuw proberen"}</button>
        </div>
      )}

      {i && (
        <div>
          <div style={{ marginTop: 12, background: "rgba(245,185,66,.1)", border: "1px solid rgba(245,185,66,.35)", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: C.text }}>
            <strong>Eerste actie:</strong> {i.eerste_actie}
          </div>
          <Kop>In het kort</Kop><Blok>{i.samenvatting}</Blok>
          <Kop>Wat de klant wil bereiken</Kop><Blok>{i.doel_klant}</Blok>
          <Kop>Wat we weten (uit de aanvraag)</Kop><ul style={lijstStijl}>{(i.wat_we_weten || []).map((x, n) => <li key={n}>{x}</li>)}</ul>
          <Kop>Aannames om te toetsen</Kop><ul style={lijstStijl}>{(i.aannames || []).map((x, n) => <li key={n}>{x}</li>)}</ul>
          <Kop>Vragen voor het gesprek</Kop>
          {(i.vragen || []).map((t, ti) => (
            <div key={ti} style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 13, fontWeight: 700, margin: "10px 0 4px", color: C.text }}>{t.thema}</p>
              {t.vragen.map((v, vi) => {
                const k = ti + "_" + vi;
                return (
                  <label key={vi} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "5px 0", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!afgevinkt[k]} onChange={() => vink(k)} style={{ marginTop: 4, flexShrink: 0, width: "auto" }} />
                    <span style={{ fontSize: 13, lineHeight: 1.55, color: afgevinkt[k] ? C.dim : C.text, textDecoration: afgevinkt[k] ? "line-through" : "none" }}>
                      {v.vraag}
                      <span style={{ display: "block", fontSize: 11, color: C.dim, textDecoration: "none" }}>Waarom: {v.waarom}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
          <Kop>Mogelijke oplossingsrichting</Kop><Blok>{i.oplossingsrichting}</Blok>
          <Kop>Voorstel voor de aanpak</Kop>
          {(i.aanpak || []).map((f, n) => <p key={n} style={{ fontSize: 13, lineHeight: 1.6, margin: "6px 0", color: C.text }}><strong>{f.fase}:</strong> {f.inhoud}</p>)}
          <Kop>Risico's en aandachtspunten</Kop><ul style={lijstStijl}>{(i.risicos || []).map((x, n) => <li key={n}>{x}</li>)}</ul>
          <Kop>Budget en planning</Kop><Blok>{i.budget_en_planning}</Blok>
          <Kop>Gespreksopzet</Kop>
          <p style={{ fontSize: 13, lineHeight: 1.6, margin: "4px 0", color: C.text }}><strong>Opening:</strong> {i.gespreksopzet?.opening}</p>
          <p style={{ fontSize: 13, lineHeight: 1.6, margin: "4px 0", color: C.text }}><strong>Afsluiting:</strong> {i.gespreksopzet?.afsluiting}</p>
          <p style={{ fontSize: 11, color: C.dim, marginTop: 14 }}>Automatisch gemaakt op basis van alleen wat de klant zelf invulde. Controleer de aannames in het gesprek.</p>
        </div>
      )}

      {bericht && <p style={{ fontSize: 12, color: C.yellow, marginTop: 10 }}>{bericht}</p>}
    </div>
  );
}
